import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { PENDING_ORG_INVITE_TOKEN_KEY } from '../constants/orgInvite'
import { getApiBaseUrl, authAPI } from '../services/api'
import { buildSsoStartUrl, postSignInDestination, consentResumeReturnTo } from '../context/ssoNavigation'
import { saveContinuation, loadContinuation, boundedContext, clearContinuation, appendContinuation } from '../context/oauthContinuation'
import { SIGNED_IN_HOME } from '../customerBuild'

const PROVIDER_VALUE = { google: 'GoogleOAuth', microsoft: 'MicrosoftOAuth' }

// Friendly messages for an SSO callback that redirected back to /login?sso_error=<code> (the backend no longer shows raw JSON
// for user-actionable failures). `no_account` = the identity was valid but no Rithmo account is linked / shares its email yet.
const SSO_ERROR_MESSAGE = {
  no_account: 'We couldn’t find a Rithmo account for that sign-in. Create an account (or sign in with your email) first, then use Continue with Google.',
}
function ssoErrorMessage(code) {
  if (!code) return ''
  return SSO_ERROR_MESSAGE[code] || 'Single sign-on didn’t complete. Please try again, or sign in with your email.'
}

/**
 * The customer sign-in page's flow: the same steps, in the same order, as the shared-signin LoginPage (pages/LoginPage.jsx,
 * shared-signin Stage 3), so the customer build keeps its accepted behavior:
 * - back to the exact page that sent here, query included (e.g. /mcp-authorize keeps its authorization handle);
 * - an MCP consent continuation arriving on the /login URL is kept in the bounded session carrier and resumed once;
 * - an org invitation (?token=) continues to /join, kept across email verification;
 * - an unverified account goes to /verify-email;
 * - Google / Microsoft and company SSO are offered only when the server reports them enabled.
 * Only the page around it differs. A change to LoginPage's flow must be made here too.
 */
export function useSignInFlow() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const from = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ''}`
    : SIGNED_IN_HOME

  // Company SSO entry (/login?sso_org=<org>): offered only when the server reports that org has company SSO enabled.
  const ssoOrg = searchParams.get('sso_org')?.trim() || ''
  const [company, setCompany] = useState({ org: '', available: false })
  useEffect(() => {
    if (!ssoOrg) return undefined
    let cancelled = false
    authAPI.getSsoAvailability(ssoOrg)
      .then((r) => { if (!cancelled) setCompany({ org: ssoOrg, available: !!(r?.data?.company ?? r?.company) }) })
      .catch(() => { if (!cancelled) setCompany({ org: ssoOrg, available: false }) })
    return () => { cancelled = true }
  }, [ssoOrg])
  const companyAvailable = !!ssoOrg && company.org === ssoOrg && company.available

  // An SSO round-trip that failed redirects to /login?sso_error=<code>; surface a friendly message (shown until the user acts).
  const ssoError = ssoErrorMessage(searchParams.get('sso_error'))

  // A consent context that arrived directly on this URL is kept in the bounded carrier (a no-op without a handle).
  useEffect(() => {
    saveContinuation(location.search)
  }, [location.search])

  // Global providers the server reports enabled (404 when the feature is off: none).
  const [ssoMethods, setSsoMethods] = useState([])
  useEffect(() => {
    let cancelled = false
    authAPI.getSsoMethods()
      .then((r) => { if (!cancelled) setSsoMethods((r?.data?.methods ?? r?.methods ?? []).filter((m) => m === 'google' || m === 'microsoft')) })
      .catch(() => { if (!cancelled) setSsoMethods([]) })
    return () => { cancelled = true }
  }, [])

  // URL first: this request's handle on the /login URL wins over a possibly stale stored authorization.
  const resolveReturnTo = () => consentResumeReturnTo(boundedContext(location.search) || loadContinuation()) || from

  const startCompanySso = () => {
    if (!ssoOrg) return
    window.location.href = buildSsoStartUrl({ apiBase: getApiBaseUrl(), org: ssoOrg, returnTo: resolveReturnTo() })
  }

  const startGlobalSso = (method) => {
    const base = getApiBaseUrl().replace(/\/+$/, '')
    window.location.href = `${base}/auth/sso/start?provider=${encodeURIComponent(PROVIDER_VALUE[method])}&return_to=${encodeURIComponent(resolveReturnTo())}`
  }

  const signIn = async (email, password) => {
    setError('')
    setLoading(true)
    try {
      const user = await login(email, password)
      const inviteToken = searchParams.get('token')?.trim()
      const fromUrl = boundedContext(location.search)
      const pendingOAuthContext = fromUrl || loadContinuation()
      if (user?.emailVerified === false && inviteToken) {
        try { localStorage.setItem(PENDING_ORG_INVITE_TOKEN_KEY, inviteToken) } catch { /* ignore */ }
      }
      let dest = postSignInDestination({ emailVerified: user?.emailVerified, pendingOAuthContext, inviteToken, from })
      if (pendingOAuthContext && fromUrl && (dest.path.startsWith('/verify-email') || dest.path.startsWith('/join'))) {
        dest = { path: appendContinuation(dest.path, pendingOAuthContext) }
      }
      // The consent continuation is consumed exactly once: when it is actually resumed.
      if (dest.path.startsWith('/mcp-authorize')) clearContinuation()
      navigate(dest.path, { replace: true })
    } catch (err) {
      setError(err.message || 'Failed to sign in. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return { signIn, loading, error, ssoError, ssoMethods, companyAvailable, startCompanySso, startGlobalSso }
}
